/**
 * The stable identity of a subscription tier (VRG-365).
 *
 * A tier is identified by this key, NEVER by its display title. Titles are
 * authored in Stripe and flow onto invoices and receipts, so they change for
 * billing-document reasons that have nothing to do with the product — the live
 * catalog carried "Vera Enterprise" while every UI lookup keyed on "enterprise",
 * so every tier-specific row in the plan-comparison matrix silently resolved to
 * "not included" on every tier.
 *
 * The key is written by `seed-stripe-catalog.ts` onto each Stripe product as
 * `metadata.vera_catalog`, read back by `sync-stripe-catalog.ts` into
 * `stripe_subscription_products.tier_key`, and surfaced to the platform as
 * `SubscriptionPlan.tier_key`.
 */
export const PLAN_TIER_KEYS = [
  'starter',
  'individual',
  'business',
  // The top SELF-SERVE tier, $999. Renamed from `enterprise` in Stripe on
  // 2026-08-24 (VRG-393): "Enterprise" conventionally names the unpriced
  // contact-sales tier, so a self-serve card could not keep it once one existed.
  'scale',
  // The contact-sales tier. No Stripe price — nothing to sync, nothing to check
  // out — so no plan row carries this key today. It is listed because entitlement
  // sets below must already include it: a company closed onto this tier gets at
  // least what Scale gets, and discovering that later means discovering it as a
  // customer-facing outage.
  'enterprise',
] as const;

export type PlanTierKey = (typeof PLAN_TIER_KEYS)[number];

/**
 * Tiers that have NO Stripe price — the contact-sales end of the catalog.
 *
 * This exists because the prose above was the only thing distinguishing the two
 * meanings of `enterprise`, and prose does not fail a build. `seed-stripe-catalog.ts`
 * kept `key: 'enterprise'` on the $999 SELF-SERVE tier for two days after the Stripe
 * rename (VRG-393) landed everywhere else, and nothing anywhere could notice: the key
 * is still a member of `PLAN_TIER_KEYS`, so any "is it a valid tier key?" check passes
 * on it. Naming the unpriced set makes the real invariant — a seeder seeds priced tiers
 * and only priced tiers — something a test can assert.
 */
export const UNPRICED_TIER_KEYS: readonly PlanTierKey[] = ['enterprise'];

/**
 * The tiers `seed-stripe-catalog.ts` is responsible for: every canonical key that is
 * not contact-sales. Derived, so adding a tier to `PLAN_TIER_KEYS` extends this by
 * construction rather than by anyone remembering to.
 */
export const SELF_SERVE_TIER_KEYS: readonly PlanTierKey[] = PLAN_TIER_KEYS.filter(
  (key) => !(UNPRICED_TIER_KEYS as readonly string[]).includes(key),
);

/**
 * The tiers that carry white-label entitlements — removing "Powered by Vera",
 * serving the brand's own logo, and the hide_powered_by trigger setting.
 *
 * This exists because those three gates were written as
 * `plan.title.toLowerCase() === 'enterprise'`, in three separate files, and the
 * Stripe rename to "Vera Scale" turned all three FALSE at once — silently, with
 * no error, for every customer on the tier that had paid for the capability. It is
 * the same defect VRG-365 fixed in the comparison matrix, in code that pre-dated
 * the lesson: a tier is identified by its KEY, never by its display title, because
 * titles are billing-document copy and change for billing reasons.
 *
 * Keyed as a SET rather than a `>=` on a tier ordering: entitlement is not
 * monotonic in price by definition, and an ordering would silently grant every
 * future top tier whatever the previous one had.
 */
export const WHITE_LABEL_TIER_KEYS: readonly PlanTierKey[] = [
  'scale',
  'enterprise',
];

/** Whether a tier key (as read off `SubscriptionPlan.tier_key`) has white-label. */
export const hasWhiteLabel = (tierKey: string | null | undefined): boolean =>
  !!tierKey &&
  (WHITE_LABEL_TIER_KEYS as readonly string[]).includes(tierKey.toLowerCase());

/**
 * The tiers that carry a custom email SENDING DOMAIN (VERA-362) — Vera emails
 * come from the brand's own verified domain instead of `no-reply@vera.inc`.
 *
 * Deliberately a WIDER set than `WHITE_LABEL_TIER_KEYS`: it sits below the full
 * white-label perk (removing "Powered by Vera", Enterprise-tier only) in the
 * plan-packaging ladder — Business and up get it, per
 * `context/decisions/0004-plan-packaging.md`.
 */
export const CUSTOM_EMAIL_DOMAIN_TIER_KEYS: readonly PlanTierKey[] = [
  'business',
  'scale',
  'enterprise',
];

/** Whether a tier key (as read off `SubscriptionPlan.tier_key`) has a custom email sending domain. */
export const hasCustomEmailDomain = (tierKey: string | null | undefined): boolean =>
  !!tierKey &&
  (CUSTOM_EMAIL_DOMAIN_TIER_KEYS as readonly string[]).includes(tierKey.toLowerCase());

/**
 * The DB column is a nullable free-text mirror of Stripe metadata, so a value
 * read back from it is not guaranteed to be one of the canonical keys (a plan
 * created by hand in the Stripe dashboard can carry anything). Consumers that
 * need a canonical key narrow with this rather than casting.
 */
export const isPlanTierKey = (value: unknown): value is PlanTierKey =>
  typeof value === 'string' &&
  (PLAN_TIER_KEYS as readonly string[]).includes(value);

/**
 * The CHEAPEST tier that grants a given entitlement — e.g. for
 * `CUSTOM_EMAIL_DOMAIN_TIER_KEYS` (`['business', 'scale', 'enterprise']`) this
 * is `'business'`. Entitlement sets are not necessarily written in price order,
 * so this walks `PLAN_TIER_KEYS` (which IS price-ordered) rather than reading
 * `entitlementKeys[0]`.
 *
 * Exists so an "Upgrade to unlock" CTA can name the SPECIFIC plan a customer
 * actually needs ("Upgrade to Business") instead of a generic "Upgrade to
 * unlock" — different features unlock at different tiers, and a generic CTA
 * doesn't tell the customer which plan solves their problem.
 */
export const getMinimumTierKey = (
  entitlementKeys: readonly PlanTierKey[],
): PlanTierKey | undefined =>
  PLAN_TIER_KEYS.find((key) =>
    (entitlementKeys as readonly string[]).includes(key),
  );
